import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { loginWithSsoToken } from "../../actions/authActions";

// Landing page for the cross-server "Open file" hand-off from the verifier
// dashboard's My work area. A verifier's active block can live on a
// different server than the one they're logged into — Open file there
// sends them here with a short-lived single-use token (minted by
// POST /verifierQueue/openFileToken on the SOURCE server) instead of
// asking for a password again. This page redeems it (POST /ssoLogin,
// which itself calls back to the source server to consume the token) and,
// on success, lands the verifier straight on the block they came for.
//
// Query params: token, email, src (the minting server's name), patientId,
// lastDatasetId. Only token/email/src are trusted by the backend — patientId
// and lastDatasetId are just where to navigate afterward, not a security
// boundary (the route itself re-checks the verifier owns that patient).
class SsoRedirect extends Component {
  state = { status: "signing-in", error: "" };

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    const token = params.get("token");
    const email = params.get("email");
    const src = params.get("src");
    const patientId = params.get("patientId");
    const lastDatasetId = params.get("lastDatasetId");

    if (!token || !email || !src) {
      this.setState({ status: "error", error: "This sign-in link is missing information." });
      return;
    }

    this.props.loginWithSsoToken({ token, email, src }).then((result) => {
      if (!result || !result.ok) {
        this.setState({
          status: "error",
          error: (result && result.error) || "Sign-in failed.",
        });
        return;
      }
      if (patientId && lastDatasetId) {
        // Same type-10 routing rule as the same-server "Open file" path in
        // VerifierProfile.js — see there for why.
        const basePath = localStorage.getItem("verifiertype") === "10" ? "/verifier-chart" : "/verifier-unanalysed";
        this.props.history.replace(`${basePath}/${patientId}/${lastDatasetId}`);
      } else {
        // No specific file to open (shouldn't normally happen) — land on
        // the verifier's own dashboard instead of leaving them stranded.
        this.props.history.replace("/verifier-profile");
      }
    });
  }

  render() {
    const { status, error } = this.state;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0f172a",
          color: "#e2e8f0",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 360, padding: 24 }}>
          {status === "signing-in" && (
            <>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Signing you in…</div>
              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.7 }}>
                Opening your file on this server.
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#ff6b6b" }}>
                Couldn't open this file
              </div>
              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>{error}</div>
              <div style={{ marginTop: 16, fontSize: 12, opacity: 0.6 }}>
                Go back to your dashboard and try Open file again, or sign in here manually.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

SsoRedirect.propTypes = {
  loginWithSsoToken: PropTypes.func.isRequired,
};

export default connect(null, { loginWithSsoToken })(withRouter(SsoRedirect));
